import { useEffect, useState } from 'react';
import { api } from '../api.js';
import { friendlyDeviceTypeLabel, lastHeardText, OFFLINE_CONFIRM_MIN } from '../devices.js';
import { AddDeviceSheet } from './AddDeviceSheet.jsx';
import { useConfirm } from './ConfirmDialog.jsx';

// Per-tank device list, mounted under the Equipment section. Mirrors the
// HydrosLinkPanel pattern (one block at the top of the section) but
// handles many devices per tank — the device-type registry in
// src/devices.js drives the friendly label per row.
// Pairing and removing a display are the tank owner's: the claim refuses
// any other tank, so an editor or viewer sees the list read-only.

// listed: false on Connect a device (UI step 4), where each display already
// has its own page: only pairing a new one.
export function DevicesPanel({ tank, onReload, listed = true, onPaired = null }) {
  const confirm = useConfirm();
  const isOwner = !tank.role || tank.role === 'owner';
  const [devices, setDevices] = useState(null);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(null);
  const [showAdd, setShowAdd] = useState(false);

  const reload = async () => {
    try {
      const r = await api(`/api/tanks/${tank.id}/devices`);
      setDevices(r.devices || []);
    } catch (e) {
      setErr(e.message || 'Could not load devices.');
      setDevices([]);
    }
  };

  useEffect(() => {
    reload();
  }, [tank.id]);

  const unlink = async (device) => {
    // Unlinking frees the display from the whole account (it drops every
    // tank and must be paired again), not just from this tank.
    const ok = await confirm({
      title: `Remove ${device.display_name || 'this display'} from your account?`,
      body: 'It stops showing your tanks and sending readings. To use it again, pair it from its screen. To show another tank instead, move it in Account, TankSensei devices.',
      confirmLabel: 'Remove',
      destructive: true,
    });
    if (!ok) return;
    setBusy(device.id);
    setErr('');
    try {
      await api(`/api/tanks/${tank.id}/devices/${device.id}`, { method: 'DELETE' });
      await reload();
      await onReload?.();
    } catch (e) {
      setErr(e.message || 'Could not remove it.');
    } finally {
      setBusy(null);
    }
  };

  const onAdded = async () => {
    setShowAdd(false);
    await reload();
    await onReload?.();
    await onPaired?.();
  };

  return (
    <div className="ts-add-form" style={{ marginBottom: 14 }}>
      <div className="ts-add-head">
        <h4 className="font-heading">Printed Reef TankSensei devices</h4>
        <span className="ts-hint">Displays and probes that publish to this tank</span>
      </div>

      {err && (
        <div role="alert" className="ts-err">
          {err}
        </div>
      )}

      {!listed ? (
        isOwner && (
          <p className="ts-hint" style={{ marginTop: 8 }}>
            To add a display, open its pairing screen (on the 7-inch display: Settings, then Pair
            with phone), then tap Add a device and scan the QR.
          </p>
        )
      ) : devices === null ? (
        <p className="ts-hint" style={{ marginTop: 8 }}>
          Loading…
        </p>
      ) : devices.length === 0 ? (
        <p className="ts-hint" style={{ marginTop: 8 }}>
          {isOwner
            ? 'Nothing linked yet. To add a display, open its pairing screen (on the 7-inch display: Settings, then Pair with phone), then tap Add a device and scan the QR.'
            : 'No displays are linked to this tank. Only the tank owner can add one.'}
        </p>
      ) : (
        <ul className="ts-device-list" style={{ listStyle: 'none', padding: 0, margin: '8px 0' }}>
          {devices.map((d) => (
            <li
              key={d.id}
              className="ts-row-body"
              style={{
                alignItems: 'center',
                padding: '8px 0',
                borderTop: '1px solid var(--border-soft, rgba(0,0,0,0.08))',
              }}
            >
              <div style={{ flex: '1 1 auto' }}>
                <strong>
                  {d.display_name || friendlyDeviceTypeLabel(d.device_type, d.display || 'Device')}
                </strong>
                <p className="ts-hint" style={{ margin: '2px 0 0' }}>
                  {[d.client_id, d.fw_version ? `software ${d.fw_version}` : null]
                    .filter(Boolean)
                    .join(' · ')}
                </p>
                {deviceSilence(d.last_seen_at) ? (
                  <p
                    className="ts-hint"
                    style={{ margin: '2px 0 0', color: 'var(--amber, #f0b429)', fontWeight: 600 }}
                  >
                    {deviceSilence(d.last_seen_at)} ({formatAgo(d.last_seen_at)}). Check its power
                    and Wi-Fi.
                  </p>
                ) : (
                  <p className="ts-hint" style={{ margin: '2px 0 0' }}>
                    Last heard {formatAgo(d.last_seen_at)}
                  </p>
                )}
              </div>
              {isOwner && (
                <button
                  type="button"
                  className="ts-chip ts-chip-danger"
                  onClick={() => unlink(d)}
                  disabled={busy === d.id}
                >
                  {busy === d.id ? '…' : 'Remove'}
                </button>
              )}
            </li>
          ))}
        </ul>
      )}

      {!isOwner ? null : showAdd ? (
        <AddDeviceSheet tank={tank} onDone={onAdded} onCancel={() => setShowAdd(false)} />
      ) : (
        <button type="button" className="ts-add-btn" onClick={() => setShowAdd(true)}>
          Add a device
        </button>
      )}
    </div>
  );
}

// Past the confirm window the server pages the keeper that the display
// stopped reporting, so the panel says it in words too, not only as a grey
// "3d ago".
export { OFFLINE_CONFIRM_MIN };

export function deviceSilence(iso, now = Date.now()) {
  if (!iso) return null;
  const t = new Date(iso).getTime();
  if (!Number.isFinite(t) || now - t < OFFLINE_CONFIRM_MIN * 60 * 1000) return null;
  return `Not reporting since ${lastHeardText(iso, now)}`;
}

export function formatAgo(iso) {
  if (!iso) return 'never';
  const s = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}
